// Keyboard navigation wiring for TaskListBase.
import { useCallback } from 'react';
import useTaskListKeyboardNavigation from '@/features/tasks/hooks/list/use-list-keyboard-nav';
import { useKeyboardNavProps } from '@/features/tasks/hooks/list/use-list-keyboard-nav-props';

export function useTaskListBaseKeyboard({
  tasks,
  allTasks,
  selectedIndex,
  selectSingleTask,
  deselectAllTasks,
  openNewTaskInput,
  handleToggleComplete,
  handleDeleteTask,
  updateTask,
  triggerFollowUp,
  handleStartFocus,
  stopFocus,
  activeFocusTaskId,
  user,
  onStartEditing,
  editingTaskId,
  isFocused,
  view,
  planMode,
  newTaskPosition,
  disableStructuralNav,
  disableRowNavigate,
  selectedTaskIds,
  cancel,
  handleStartAddingSubtask,
  addingSubtaskTo,
}) {
  const handleKeyboardSelect = useCallback(
    (idOrIndex) => {
      if (typeof idOrIndex === 'string') {
        selectSingleTask(idOrIndex);
      } else if (typeof idOrIndex === 'number') {
        if (idOrIndex === -1) deselectAllTasks();
        else if (tasks[idOrIndex]) selectSingleTask(tasks[idOrIndex].id);
      }
    },
    [tasks, selectSingleTask, deselectAllTasks],
  );

  const keyboardNavProps = useKeyboardNavProps({
    tasks,
    allTasks,
    selectedIndex,
    handleKeyboardSelect,
    openNewTaskInput,
    handleToggleComplete,
    handleDeleteTask,
    updateTask,
    triggerFollowUp,
    handleStartFocus,
    stopFocus,
    activeFocusTaskId,
    user,
    onStartEditing,
    editingTaskId,
    deselectAllTasks,
    isFocused,
    view,
    planMode,
    isNewTaskInputActive: !!newTaskPosition,
    disableStructuralNav,
    disableRowNavigate,
    handleStartAddingSubtask,
    addingSubtaskTo,
  });

  const { previousSelectedIndexRef } = useTaskListKeyboardNavigation(
    keyboardNavProps ?? {
      filteredTasks: [],
      allTasks: [],
      setSelectedIndex: () => {},
      selectedIndex: -1,
    },
  );

  const handleCancelNewTaskInput = useCallback(
    (_restoreFocus = false) => {
      if (selectedTaskIds.length === 0) {
        const prev = previousSelectedIndexRef.current;
        if (prev !== null && tasks[prev]) selectSingleTask(tasks[prev].id);
      }
      cancel();
    },
    [
      previousSelectedIndexRef,
      tasks,
      selectSingleTask,
      cancel,
      selectedTaskIds,
    ],
  );

  return { handleCancelNewTaskInput };
}
